import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import PublicPreview from "@/pages/PublicPreview";
import PortalPaused from "@/pages/PortalPaused";
import Calculator from "@/pages/buyer/Calculator";
import { BuyerLayout } from "@/components/BuyerLayout";
import BuyerDashboard from "@/pages/buyer/BuyerDashboard";
import Onboarding from "@/pages/buyer/Onboarding";
import Checklist from "@/pages/buyer/Checklist";
import Journey from "@/pages/buyer/Journey";
import TouringPrep from "@/pages/buyer/TouringPrep";
import TourSchedule from "@/pages/buyer/TourSchedule";
import Compare from "@/pages/buyer/Compare";
import OfferReadiness from "@/pages/buyer/OfferReadiness";
import Resources from "@/pages/buyer/Resources";
import Assistant from "@/pages/buyer/Assistant";
import VendorGuide from "@/pages/buyer/VendorGuide";
import Glossary from "@/pages/buyer/Glossary";
import Anniversary from "@/pages/buyer/Anniversary";
import AgentDashboard from "@/pages/agent/AgentDashboard";
import ClientDetail from "@/pages/agent/ClientDetail";
import PreviewClient from "@/pages/agent/PreviewClient";
import VendorManager from "@/pages/agent/VendorManager";

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  return <Navigate to={user.role === "agent" ? "/agent" : "/app/dashboard"} replace />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/invite" element={<AcceptInvite />} />
      <Route path="/paused" element={<PortalPaused />} />
      <Route path="/preview/:token" element={<PublicPreview />} />

      <Route
        path="/app"
        element={
          <ProtectedRoute role="buyer">
            <BuyerLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<BuyerDashboard />} />
        <Route path="onboarding" element={<Onboarding />} />
        <Route path="journey" element={<Journey />} />
        <Route path="touring" element={<TouringPrep />} />
        <Route path="tours" element={<TourSchedule />} />
        <Route path="compare" element={<Compare />} />
        <Route path="calculator" element={<Calculator />} />
        <Route path="offer" element={<OfferReadiness />} />
        <Route path="resources" element={<Resources />} />
        <Route path="vendors" element={<VendorGuide />} />
        <Route path="glossary" element={<Glossary />} />
        <Route path="anniversary" element={<Anniversary />} />
        <Route path="assistant" element={<Assistant />} />
      </Route>

      <Route
        path="/agent"
        element={
          <ProtectedRoute role="agent">
            <AgentDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/clients/:id"
        element={
          <ProtectedRoute role="agent">
            <ClientDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/clients/:id/preview"
        element={
          <ProtectedRoute role="agent">
            <PreviewClient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agent/vendors"
        element={
          <ProtectedRoute role="agent">
            <VendorManager />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
